import { useEffect, useMemo, useRef, useState } from 'react';
import type { Roster } from '@archon3d/core';

/**
 * One offscreen renderer for the whole palette, never one WebGL context per piece. A portrait
 * shows a character's model: the portraits are drawn again when a model changes, not at every
 * keystroke in a character's statistics, each of which is a new roster (editor:quality#4).
 */
export function usePortraits(roster: Roster) {
  const [images, setImages] = useState<Record<string, string>>({});
  const models = useMemo(
    () => JSON.stringify(Object.values(roster).map((c) => [c.id, c.model ?? null])),
    [roster],
  );
  const latest = useRef(roster);
  latest.current = roster;
  useEffect(() => {
    let cancelled = false;
    void import('./portraitRenderer')
      .then(async ({ renderPortraits }) => {
        await renderPortraits(
          latest.current,
          () => cancelled,
          (id, image) => {
            if (!cancelled)
              setImages((previous) =>
                previous[id] === image ? previous : { ...previous, [id]: image },
              );
          },
        );
      })
      .catch(() => {
        /* Text fallback stays available when WebGL is unavailable. */
      });
    return () => {
      cancelled = true;
    };
  }, [models]);
  return images;
}
